import { WalletModalProvider } from "@solana/wallet-adapter-ant-design";
import { ConnectionProvider, WalletProvider } from "@solana/wallet-adapter-react";
import {
  Coin98WalletAdapter,
  LedgerWalletAdapter,
  MathWalletAdapter,
  PhantomWalletAdapter,
  SolflareWalletAdapter,
  SolongWalletAdapter,
  TorusWalletAdapter,
  XDEFIWalletAdapter,
} from "@solana/wallet-adapter-wallets";
import {
  createDefaultAddressSelector,
  createDefaultAuthorizationResultCache,
  createDefaultWalletNotFoundHandler,
  SolanaMobileWalletAdapter,
} from "@solana-mobile/wallet-adapter-mobile";
import type { FC } from "react";
import React, { useMemo, useState } from "react";

import { STEP_NETWORK_CONFIG_MAP } from "../utils/network";
import { PublicKeyWalletAdapter } from "../utils/wallet/adapters/publicKey";
import { useNetworkConfig } from "./app/config";

interface WalletContextProviderProps {
  children: React.ReactNode;
}

export const WalletContextProvider: FC<WalletContextProviderProps> = ({ children }) => {
  const { network } = useNetworkConfig();
  const endpoint = STEP_NETWORK_CONFIG_MAP[network].endpoint;

  const [publicKeyWalletAdapter, _setPublicKeyWalletAdapter] = useState<PublicKeyWalletAdapter>(
    new PublicKeyWalletAdapter(),
  );

  const wallets = useMemo(
    () => [
      new SolanaMobileWalletAdapter({
        addressSelector: createDefaultAddressSelector(),
        appIdentity: { name: "Step Finance", uri: "https://app.step.finance", icon: "logo192.png" },
        authorizationResultCache: createDefaultAuthorizationResultCache(),
        cluster: network,
        onWalletNotFound: createDefaultWalletNotFoundHandler(),
      }),
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter({ network }),
      new Coin98WalletAdapter(),
      new SolongWalletAdapter(),
      new LedgerWalletAdapter(),
      new TorusWalletAdapter(),
      new MathWalletAdapter(),
      new XDEFIWalletAdapter(),
      publicKeyWalletAdapter,
    ],
    [network, publicKeyWalletAdapter],
  );

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect={true}>
        <WalletModalProvider>{children}</WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};
